import {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {getLocalTaxesIncluded, getTaxesIncluded} from 'modules/meta/selectors'
import {getCommonSearchParams} from 'modules/searchApi/selectors'

import {OldHotelOfferEntity} from '@vio/core/src/offer/business/offersMapping'
import {
  useGetHotelsOffersQuery,
  useGetHotelsQuery
} from '@vio/core/src/sapi/services/searchApi'

import {BetterValueHotel} from '../components/BetterValueCarousel/types'

const MIN_BETTER_VALUE_HOTELS = 4
const MAX_BETTER_VALUE_HOTELS = 10

export interface UseBetterValueHotelsParams {
  similarHotelsIds?: string[]
  enabled?: boolean
  checkIn?: string
  checkOut?: string
  rooms?: string
  currency?: string
  /**
   * Current hotel's NIGHTLY price. The carousel filter compares this to each
   * candidate hotel's `cheapestOffer.nightlyRate` (also nightly), so both
   * sides must be in the same per-night unit. Passing a per-stay total here
   * lets pricier hotels slip through when the user has `showTotalPrices` on
   * (their nightly may be lower than the current hotel's total even though
   * their total is higher).
   */
  offerPrice?: number
}

interface UseBetterValueHotelsReturn {
  betterValueHotels: BetterValueHotel[]
  offersData: Record<string, OldHotelOfferEntity> | undefined
  isLoading: boolean
  isError: boolean
}

/**
 * Hook to fetch hotel content and offers for similar hotels.
 * Used by the BetterValueCarousel to display alternative hotels
 * when current hotel prices are higher than similar hotels.
 */
export const useBetterValueHotels = (
  params?: UseBetterValueHotelsParams
): UseBetterValueHotelsReturn => {
  const {
    similarHotelsIds = [],
    enabled = true,
    checkIn,
    checkOut,
    rooms,
    currency,
    offerPrice
  } = params || {}

  const includeTaxes = useSelector(getTaxesIncluded)
  const includeLocalTaxes = useSelector(getLocalTaxesIncluded)
  const commonSearchParams = useSelector(getCommonSearchParams)

  const limitedHotelIds = similarHotelsIds.slice(0, MAX_BETTER_VALUE_HOTELS)

  const hasRequiredParams =
    checkIn &&
    checkOut &&
    rooms &&
    currency &&
    limitedHotelIds.length >= MIN_BETTER_VALUE_HOTELS

  const shouldFetch = enabled && hasRequiredParams

  // Fetch hotel content (name, image, star rating, reviews)
  const {
    data: hotelsData,
    isLoading: isHotelsLoading,
    isError: isHotelsError
  } = useGetHotelsQuery({hotelIds: limitedHotelIds}, {skip: !shouldFetch})

  // Fetch offers for the hotels
  const {
    data: offersData,
    isLoading: isOffersLoading,
    isError: isOffersError
  } = useGetHotelsOffersQuery(
    {
      hotelIds: limitedHotelIds,
      checkIn: checkIn!,
      checkOut: checkOut!,
      rooms: rooms!,
      roomsNumber: 1,
      includeTaxes,
      includeLocalTaxes,
      cugDeals: commonSearchParams.cugDeals,
      tier: commonSearchParams.tier
    },
    {skip: !shouldFetch}
  )

  // Combine hotel content with offers
  const betterValueHotels = useMemo((): BetterValueHotel[] => {
    if (!hotelsData || !offersData) return []

    const hotels = limitedHotelIds
      .map(hotelId => {
        const hotel = hotelsData[hotelId]
        const offerEntity = offersData[hotelId]

        if (!hotel || !offerEntity) return null

        // Get the cheapest offer
        const offers = offerEntity.offers || []
        const cheapestOffer = offers[0]

        if (!cheapestOffer) return null

        const betterValueHotel: BetterValueHotel = {
          hotelId,
          hotelName: hotel.hotelName || '',
          starRating: hotel.starRating || 0,
          reviewScore: hotel.guestRating?.overall || 0,
          reviewCount: hotel.reviewCount || 0,
          imageUrl: hotel.imageURIs?.[0] || '',
          price: cheapestOffer.nightlyRate || cheapestOffer.rate?.base || 0,
          totalPrice: cheapestOffer.totalRate || 0,
          anchorPrice: offerEntity.anchorPriceRateBreakdown?.nightlyRate,
          anchorTotalPrice: offerEntity.anchorPriceRateBreakdown?.totalRate,
          currency: currency!,
          offerId: cheapestOffer.id
        }
        return betterValueHotel
      })
      .filter((hotel): hotel is BetterValueHotel => hotel !== null)
      .filter(hotel => offerPrice === undefined || hotel.price < offerPrice)

    return hotels.length >= MIN_BETTER_VALUE_HOTELS ? hotels : []
  }, [hotelsData, offersData, limitedHotelIds, currency, offerPrice])

  return {
    betterValueHotels,
    offersData,
    isLoading: isHotelsLoading || isOffersLoading,
    isError: isHotelsError || isOffersError
  }
}
